import { Box } from '@material-ui/core'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'usehooks-ts'

import Button from '@/components/button'
import { useAuth } from '@/hooks/use-auth'
import { useCompanies } from '@/hooks/use-companies'
import { FieldInfo } from '@/pages/maps/contexts/region-context'
import { routes } from '@/routes/config'

import { useRegionContext } from '../contexts/region-context'
import { filterValidFields } from '../utils/field-validation'
import { saveSelection } from '../utils/maps-selections-storage'
import { filteredItems } from '../utils/search-fields'
import FeatureDescription from './components/feature-description'
import SearchBarFields from './components/search-bar-fields'
import SelectRegion from './components/select-region'
import { TotalAreaPaper } from './components/total-area-paper'
import WrapperFieldsList from './components/wrapper-fields-list'
import { useStyles } from './styles'

interface FieldInfoWithChecked extends FieldInfo {
  checked: boolean
}

export default function LateralPanel() {
  const classes = useStyles()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { selectedCompany } = useCompanies()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 1000)

  const {
    selectedRegion,
    selectedProject,
    isLoadingRegion,
    regionFieldsInfo,
    toggleFieldSelection,
    selectedFields,
    setSelection,
    clearSelectionFieldIds,
  } = useRegionContext()

  const { validFields, selectedItems, availableItems } = useMemo(() => {
    const valid = filterValidFields(regionFieldsInfo) as FieldInfoWithChecked[]
    return {
      validFields: valid,
      selectedItems: valid.filter((field) => field.checked),
      availableItems: valid.filter((field) => !field.checked),
    }
  }, [regionFieldsInfo])

  const filteredAvailableItems = filteredItems(
    debouncedSearchTerm,
    availableItems,
  )

  const handleSubmit = () => {
    const fieldIds = selectedFields.map(({ id }) => id)

    saveSelection(
      { project_id: selectedProject!.id, region_id: selectedRegion!.id },
      user?.id,
      selectedCompany?.id,
    )

    const targetRoute = routes.DiagnosesCompare(
      selectedProject!.id.toString(),
      selectedRegion!.id.toString(),
    )

    const searchParams = new URLSearchParams()
    fieldIds.forEach((id) => searchParams.append('fields', id))

    navigate(`${targetRoute}?${searchParams.toString()}`)
  }

  return (
    <Box className={classes.container}>
      <FeatureDescription />
      <SelectRegion
        selectedProject={selectedProject || null}
        selectedRegion={selectedRegion || null}
        onSelectionChange={(project, region) => {
          setSelection(project.id, region.id)
          clearSelectionFieldIds()
        }}
      />
      <SearchBarFields
        regionFieldsInfo={validFields as FieldInfoWithChecked[]}
        isLoading={isLoadingRegion}
        search={{ searchTerm, setSearchTerm }}
      />
      <WrapperFieldsList
        title='Selecione um ou mais talhões que deseja analisar:'
        defaultText='Nenhum talhão disponível para análise'
        items={filteredAvailableItems as FieldInfoWithChecked[]}
        onToggle={toggleFieldSelection}
        isLoading={isLoadingRegion}
      />
      <WrapperFieldsList
        title='Talhões selecionados:'
        defaultText='Nenhum talhão selecionado'
        items={selectedItems as FieldInfoWithChecked[]}
        onToggle={toggleFieldSelection}
        isLoading={isLoadingRegion}
      />
      <TotalAreaPaper selectedItems={validFields as FieldInfoWithChecked[]} />
      <Button
        disabled={!selectedRegion || !selectedFields.length}
        color='primary'
        className={classes.analyzeButton}
        onClick={handleSubmit}
      >
        Analisar área selecionada
      </Button>
    </Box>
  )
}
